export type CategoryBand = {
  code: number
  label: string
  min: number
  minInclusive: boolean
}

/** First matching band wins. Bands are listed from the best condition to the worst. */
export function classifyCategoryBand(
  value: number,
  bands: readonly CategoryBand[]
): number | undefined {
  if (!Number.isFinite(value)) return undefined

  for (const band of bands) {
    const matches = band.minInclusive ? value >= band.min : value > band.min
    if (matches) return band.code
  }

  return undefined
}

export function categoryBandLabel(
  code: number,
  bands: readonly CategoryBand[]
): string | undefined {
  return bands.find((band) => band.code === code)?.label
}

/** A "< N%" band excludes N, so that reading falls into the next band. Repair includes 20% wastage. */
export const DFN_CATEGORY_BANDS = [
  { code: 0, label: 'No wastage', min: 0, minInclusive: true },
  {
    code: 1,
    label: 'Acceptable: < 10% wastage',
    min: -10,
    minInclusive: false,
  },
  {
    code: 2,
    label: 'Caution: ≥ 10% and < 15% wastage',
    min: -15,
    minInclusive: false,
  },
  {
    code: 3,
    label: 'Caution: ≥ 15% and < 20% wastage',
    min: -20,
    minInclusive: false,
  },
  {
    code: 4,
    label: 'Repair area: ≥ 20% wastage',
    min: Number.NEGATIVE_INFINITY,
    minInclusive: true,
  },
] as const satisfies readonly CategoryBand[]

export const EMAT_COMBINED_FALLBACK_LABELS: Record<number, string> = {
  0: 'No Wastage Data Available',
  1: 'SN/V - Condition not verifiable',
}

export const EMAT_COMBINED_CATEGORY_BANDS = [
  {
    code: 2,
    label: 'S1 - No discernable metal loss',
    min: 0,
    minInclusive: false,
  },
  {
    code: 3,
    label: 'S2 - Minimal wastage (0-5%)',
    min: -5,
    minInclusive: false,
  },
  {
    code: 4,
    label: 'S3 - Moderate wastage (5-20%)',
    min: -20,
    minInclusive: false,
  },
  {
    code: 5,
    label: 'S4 - Significant wastage (20-25%)',
    min: -25,
    minInclusive: false,
  },
  {
    code: 6,
    label: 'S5 - Severe wastage (>25%)',
    min: Number.NEGATIVE_INFINITY,
    minInclusive: true,
  },
] as const satisfies readonly CategoryBand[]

export function ematCombinedCategoryLabel(code: number): string | undefined {
  return (
    EMAT_COMBINED_FALLBACK_LABELS[code] ??
    categoryBandLabel(code, EMAT_COMBINED_CATEGORY_BANDS)
  )
}
